// src/v3/ui/symbol/symbol.hooks.ts

import { isRecord, SYMBOL_PRICE_RETURN_MODE_OBJ } from "@signals/shared";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, qk } from "../_common";
import { SYMBOL_AI_ANALYSIS_SECTION_OBJ, type TSymbolAiAnalysisSection } from "../_deps";
import type { FilterClause, SortClause } from "../_deps";

export type SymbolItem = {
  id: string;
  symbol: string;
  name: string | null;
  exchange: string | null;
  price: number | null;
  volume: number | null;
  marketCap: number | null;
  avgVolume: number | null;
  sharesOutstanding: number | null;
  autoStatus: string;
  manualStatus: string | null;
  previousSymbols: string[] | null;
  financials: Record<string, unknown>;
  apiData: {
    fmp?: { updatedAt?: string | Date } | null;
    [key: string]: unknown;
  };
  relations: {
    industry?: { name: string } | null;
    country?: { name: string } | null;
    company?: { name: string; description: string | null } | null;
    exchange?: { key: string; fullName: string } | null;
    tradeCurrency?: { key: string; name: string } | null;
    reportedCurrency?: { key: string; name: string } | null;
  } | null;
  timestamp?: { createdAt: Date; updatedAt?: Date };
};

// ── TanStack Query hooks ────────────────────────────────────────

export interface SymbolListFilters {
  limit?: number;
  offset?: number;
  filters?: FilterClause[];
  sort?: SortClause[];
}

/** Read: list symbols with pagination/filter/sort. */
export function useSymbols(filters?: SymbolListFilters) {
  const searchParams = new URLSearchParams();
  if (filters?.limit) searchParams.set("limit", String(filters.limit));
  if (filters?.offset) searchParams.set("offset", String(filters.offset));
  if (filters?.filters && filters.filters.length > 0) {
    searchParams.set("filters", JSON.stringify(filters.filters));
  }
  if (filters?.sort && filters.sort.length > 0) {
    searchParams.set(
      "sort",
      filters.sort.map((s) => `${s.column}:${s.direction}`).join(","),
    );
  }
  const qs = searchParams.toString();
  return useQuery({
    queryKey: qk.symbols.list(filters ?? {}),
    queryFn: () =>
      apiFetch<{ items: SymbolItem[]; total: number }>(
        `/api/symbols${qs ? `?${qs}` : ""}`,
      ),
    // Смена фильтра, сортировки или страницы не должна подменять таблицу спиннером: иначе SmartTable
    // размонтируется и теряет стек отмены, раскрытую панель фильтров и режим массового редактирования.
    placeholderData: keepPreviousData,
  });
}

/** Read: single symbol detail. */
export function useSymbolDetail(id: string | null | undefined) {
  return useQuery({
    queryKey: qk.symbols.detail(id ?? ""),
    queryFn: () => apiFetch<SymbolItem>(`/api/symbols/${id}`),
    enabled: Boolean(id),
  });
}

// ── AI analysis reads ───────────────────────────────────────────────────────

export interface CompanyAnalysesData {
  summary: string;
  reports: string;
  updatedAt: string | null;
}

/** Read: company AI analyses (summary + reports). */
export function useSymbolAnalyses(symbol: string | null | undefined) {
  return useQuery({
    queryKey: qk.symbols.analyses(symbol ?? ""),
    queryFn: () =>
      apiFetch<{ items: unknown[] }>(
        `/api/symbols/${encodeURIComponent(symbol ?? "")}/analyses`,
      ),
    enabled: Boolean(symbol),
    select: (data): CompanyAnalysesData | null => {
      const value = data.items[0];
      if (isRecord(value)) {
        const item = value;
        return {
          summary: String(item["summary"] ?? ""),
          reports: String(item["reports"] ?? ""),
          updatedAt: item["updatedAt"] ? String(item["updatedAt"]) : null,
        };
      }
      return null;
    },
  });
}


/** Read: symbol history digest (markdown). */
export function useSymbolHistoryDigest(symbol: string | null | undefined) {
  return useQuery({
    queryKey: qk.symbols.historyDigest(symbol ?? ""),
    queryFn: () =>
      apiFetch<{ history: string | null }>(
        `/api/symbols/${encodeURIComponent(symbol ?? "")}/history`,
      ),
    enabled: Boolean(symbol),
    select: (data) => data.history,
  });
}

export interface AnalysisHistoryEntry {
  id: string;
  agent: string;
  target: string;
  date: string | null;
  verdict: string;
  confidence: number;
  reasoning: string;
}

/** Read: AI analysis history list for a symbol. */
export function useSymbolAiAnalyses(symbol: string | null | undefined) {
  return useQuery({
    queryKey: qk.symbols.aiAnalyses(symbol ?? ""),
    queryFn: () =>
      apiFetch<{ items: AnalysisHistoryEntry[] }>(
        `/api/symbols/ai-analyses?target=${encodeURIComponent(symbol ?? "")}&limit=100`,
      ),
    enabled: Boolean(symbol),
    select: (data) => data.items ?? [],
  });
}

// ── Mutations ───────────────────────────────────────────────────────────────

export interface SymbolCreateBody {
  symbol: string;
  name?: string;
  type?: string;
  currency?: string;
  exchange?: string;
  country?: string;
}

/** Mutation: create symbol. Invalidates symbols.list. */
export function useCreateSymbol() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SymbolCreateBody) =>
      apiFetch<SymbolItem>("/api/symbols", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.symbols.list() });
    },
  });
}

export interface SymbolUpdateBody {
  symbol?: string;
  name?: string;
  type?: string;
  currency?: string;
  exchange?: string;
  country?: string;
  [key: string]: unknown;
}

/** Mutation: update symbol by id. Invalidates symbols.list + detail. */
export function useUpdateSymbol() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: SymbolUpdateBody }) =>
      apiFetch<SymbolItem>(`/api/symbols/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: qk.symbols.list() });
      void queryClient.invalidateQueries({
        queryKey: qk.symbols.detail(variables.id),
      });
    },
  });
}

/** Mutation: delete symbol by id. Invalidates symbols.list. */
export function useDeleteSymbol() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/symbols/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.symbols.list() });
    },
  });
}

export interface SymbolPriceUpdateBody {
  price: number;
  priceDate?: string;
  reason?: string;
}

/** Mutation: update symbol closing price (manual override). Invalidates symbols + portfolio.positions
 *  (price flows into position marketValue) + portfolio.summary. */
export function useUpdateSymbolPrice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: SymbolPriceUpdateBody }) =>
      apiFetch<{ price: number }>(`/api/symbols/${id}/price`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: qk.symbols.list() });
      void queryClient.invalidateQueries({
        queryKey: qk.symbols.detail(variables.id),
      });
      void queryClient.invalidateQueries({ queryKey: qk.portfolio.all });
    },
  });
}

/** Mutation: refresh symbol price from FMP (priceSource=fmp). Invalidates symbols + portfolio. */
export function useRefreshSymbolPrice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      id: string; portfolioId: string; date: string;
      returnToFmpMode?: "preview" | "apply";
      expectedCandidate?: { price: number; priceDate: string; providerCode: string };
      expectedMarketPriceRevision?: number;
      expectedIdentityFingerprint?: string;
    }) => apiFetch<{
      status: string; operationId?: string;
      candidate?: { price: number; priceDate: string; providerCode: string; currency: string };
      affectedPortfolioIds?: string[];
      expectedMarketPriceRevision?: number;
      expectedIdentityFingerprint?: string;
    }>(`/api/symbols/${variables.id}/refresh-price?${new URLSearchParams({
      portfolioId: variables.portfolioId,
      date: variables.date,
      ...(variables.returnToFmpMode ? { returnToFmpMode: variables.returnToFmpMode } : {}),
    })}`, {
      method: "POST",
      ...(variables.returnToFmpMode === SYMBOL_PRICE_RETURN_MODE_OBJ.APPLY ? { headers: { "Content-Type": "application/json" }, body: JSON.stringify({
        expectedCandidate: variables.expectedCandidate,
        expectedMarketPriceRevision: variables.expectedMarketPriceRevision,
        expectedIdentityFingerprint: variables.expectedIdentityFingerprint,
      }) } : {}),
    }),
    onSuccess: (_data, variables) => {
      if (variables.returnToFmpMode === SYMBOL_PRICE_RETURN_MODE_OBJ.PREVIEW) return;
      void queryClient.invalidateQueries({ queryKey: qk.symbols.list() });
      void queryClient.invalidateQueries({
        queryKey: qk.symbols.detail(variables.id),
      });
      void queryClient.invalidateQueries({ queryKey: qk.portfolio.all });
      void queryClient.invalidateQueries({ queryKey: qk.portfolio.positionsForPortfolio(variables.portfolioId) });
      void queryClient.invalidateQueries({ queryKey: qk.portfolio.summaryForPortfolio(variables.portfolioId) });
      void queryClient.invalidateQueries({ queryKey: qk.portfolio.yearlyPerformance.all(variables.portfolioId) });
      void queryClient.invalidateQueries({ queryKey: qk.portfolio.yearlyPerformance.singleForPortfolio(variables.portfolioId) });
    },
  });
}

export interface ProviderIdentityMappingRequest {
  portfolioId: string;
  symbolId: string;
  providerCode: string;
  effectiveDate: string;
}

export interface ProviderIdentityMappingPreview {
  found: true;
  canApply: boolean;
  alreadyApplied: boolean;
  reason: string | null;
  symbol: { id: string; ticker: string; currency: string | null; priceSource: string };
  provider: { code: string; name: string | null; exchange: string | null; currency: string | null; isin: string | null; cusip: string | null; cik: string | null; isActivelyTrading: boolean | null };
  affectedPortfolioIds: string[];
  expectedMarketPriceRevision: number;
  expectedIdentityFingerprint: string;
}

export interface ManualPricePreviewRequest {
  portfolioId: string;
  price: number;
  priceDate: string;
  reason: string;
}

export interface ManualPricePreview {
  found: true;
  canApply: boolean;
  reason: string | null;
  symbol: { id: string; ticker: string; price: number | null; priceDate: string | null; currency: string | null; priceSource: string };
  newPrice: number;
  newPriceDate: string;
  reasonText: string;
  affectedPortfolioIds: string[];
  expectedMarketPriceRevision: number;
  expectedIdentityFingerprint: string;
}

export function usePreviewManualPrice() {
  return useMutation({
    mutationFn: ({ id, ...body }: ManualPricePreviewRequest & { id: string }) => apiFetch<ManualPricePreview>(
      `/api/symbols/${id}/price/preview`,
      { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) },
    ),
  });
}

export function useApplyManualPrice(portfolioId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, affectedPortfolioIds: _affected, ...body }: ManualPricePreviewRequest & { id: string; affectedPortfolioIds?: string[] } & Pick<ManualPricePreview, "expectedMarketPriceRevision" | "expectedIdentityFingerprint">) =>
      apiFetch<{ id: string }>(`/api/symbols/${id}/price/apply`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: qk.symbols.list() });
      void queryClient.invalidateQueries({ queryKey: qk.symbols.detail(variables.id) });
      const portfolioIds = [...new Set([portfolioId, ...(variables.affectedPortfolioIds ?? [])])];
      for (const id of portfolioIds) {
        void queryClient.invalidateQueries({ queryKey: qk.portfolio.positionsForPortfolio(id) });
        void queryClient.invalidateQueries({ queryKey: qk.portfolio.summaryForPortfolio(id) });
        void queryClient.invalidateQueries({ queryKey: qk.portfolio.compareForPortfolio(id) });
        void queryClient.invalidateQueries({ queryKey: qk.portfolio.snapshots(id) });
        void queryClient.invalidateQueries({ queryKey: qk.portfolio.yearlyPerformance.all(id) });
        void queryClient.invalidateQueries({ queryKey: qk.portfolio.yearlyPerformance.singleForPortfolio(id) });
      }
    },
  });
}

export function usePreviewProviderIdentityMapping() {
  return useMutation({
    mutationFn: (body: ProviderIdentityMappingRequest) => apiFetch<ProviderIdentityMappingPreview>(
      "/api/symbols/provider-identity/preview",
      { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) },
    ),
  });
}

export function useApplyProviderIdentityMapping(portfolioId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ affectedPortfolioIds: _affected, ...body }: ProviderIdentityMappingRequest & Pick<ProviderIdentityMappingPreview, "expectedMarketPriceRevision" | "expectedIdentityFingerprint"> & { affectedPortfolioIds?: string[] }) =>
      apiFetch<ProviderIdentityMappingPreview>("/api/symbols/provider-identity/apply", {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      }),
    onSuccess: (_result, variables) => {
      const portfolioIds = [...new Set([portfolioId, ...(variables.affectedPortfolioIds ?? [])])];
      for (const id of portfolioIds) {
        void queryClient.invalidateQueries({ queryKey: qk.portfolio.positionsForPortfolio(id) });
        void queryClient.invalidateQueries({ queryKey: qk.portfolio.summaryForPortfolio(id) });
        void queryClient.invalidateQueries({ queryKey: qk.portfolio.compareForPortfolio(id) });
        void queryClient.invalidateQueries({ queryKey: qk.portfolio.snapshots(id) });
        void queryClient.invalidateQueries({ queryKey: qk.portfolio.yearlyPerformance.all(id) });
        void queryClient.invalidateQueries({ queryKey: qk.portfolio.yearlyPerformance.singleForPortfolio(id) });
      }
    },
  });
}

// ── Bulk operation mutations ────────────────────────────────────────────────

export interface ProcessSymbolsBody {
  symbols: string[];
  skipAi?: boolean;
}

/** Mutation: process symbols (fetch data + optional AI). Invalidates symbols.list. */
export function useProcessSymbols() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ProcessSymbolsBody) =>
      apiFetch<void>("/api/symbols/process", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          symbols: body.symbols,
          ...(body.skipAi ? { skipAi: body.skipAi } : {}),
        }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.symbols.list() });
    },
  });
}

export interface RecalculateByPriceItem {
  symbol: string;
  price: number;
}

/** Mutation: recalculate symbol prices by closing price. Invalidates symbols.list. */
export function useRecalculateByPrice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (items: RecalculateByPriceItem[]) =>
      apiFetch<void>("/api/symbols/recalculate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.symbols.list() });
    },
  });
}

/** Mutation: bulk delete symbols by symbol name. Invalidates symbols.list. */
export function useDeleteSymbols() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (symbols: string[]) =>
      apiFetch<void>("/api/symbols/delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ symbols }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.symbols.list() });
    },
  });
}

// ── AI analysis mutation ────────────────────────────────────────────────────

export type AiSectionKey = TSymbolAiAnalysisSection;

export interface AnalyzeSymbolBody {
  symbol: string;
  section: AiSectionKey;
  summary?: string | undefined;
}

/**
 * Mutation: run AI analysis for a symbol section.
 * Uses a 5-minute timeout — SSE events keep the UI updated in the meantime.
 * Invalidates analyses + historyDigest + aiAnalyses on success.
 */
export function useAnalyzeSymbol() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AnalyzeSymbolBody) => {
      const payload: Record<string, unknown> = { symbol: body.symbol };
      if (body.section === SYMBOL_AI_ANALYSIS_SECTION_OBJ.REPORTS && body.summary) {
        payload["summary"] = body.summary;
      }
      return apiFetch<void>(`/api/symbols/analyze-${body.section}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        timeoutMs: 300_000,
      });
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: qk.symbols.analyses(variables.symbol),
      });
      void queryClient.invalidateQueries({
        queryKey: qk.symbols.historyDigest(variables.symbol),
      });
      void queryClient.invalidateQueries({
        queryKey: qk.symbols.aiAnalyses(variables.symbol),
      });
    },
  });
}
